import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

export type MediaType = 'image' | 'video' | 'embed' | 'iframe';
export type MediaErrorType = 'timeout' | '404' | 'network' | 'cors';

const CLASSIFY_TIMEOUT_MS = 8_000;
const trackedKeys = new Set<string>();

let activeReportId: string | null = null;

const resolveMediaType = (element: EventTarget | null): MediaType | null => {
  if (element instanceof HTMLImageElement) return 'image';
  if (element instanceof HTMLVideoElement) return 'video';
  if (element instanceof HTMLIFrameElement) return 'iframe';
  if (element instanceof HTMLEmbedElement || element instanceof HTMLObjectElement) {
    return 'embed';
  }
  return null;
};

const resolveMediaUrl = (element: Element): string => {
  if (element instanceof HTMLImageElement) return element.currentSrc || element.src || '';
  if (element instanceof HTMLVideoElement) return element.currentSrc || element.src || '';
  if (element instanceof HTMLIFrameElement) return element.src || '';
  if (element instanceof HTMLEmbedElement) return element.src || '';
  if (element instanceof HTMLObjectElement) return element.data || '';
  return '';
};

const resolveSectionName = (element: Element): string => {
  const section = element.closest('[data-report-section]') as HTMLElement | null;
  return (
    section?.dataset.reportSectionName ||
    section?.dataset.reportSection ||
    'unknown'
  );
};

const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

const trackingKey = (mediaType: MediaType, mediaUrl: string, sectionName: string) =>
  `${mediaType}:${mediaUrl}:${sectionName}`;

const classifyFromVideo = (video: HTMLVideoElement): MediaErrorType | null => {
  const code = video.error?.code;
  if (code === MediaError.MEDIA_ERR_ABORTED) return 'timeout';
  if (code === MediaError.MEDIA_ERR_NETWORK) return 'network';
  if (code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) return '404';
  if (code === MediaError.MEDIA_ERR_DECODE) return 'network';
  return null;
};

const classifyFromUrl = async (url: string): Promise<MediaErrorType> => {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
    return 'network';
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CLASSIFY_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      redirect: 'follow',
      signal: controller.signal,
    });
    if (response.status === 404 || response.status === 410) return '404';
    return 'network';
  } catch {
    if (controller.signal.aborted) return 'timeout';
    if (!isSameOriginUrl(url)) return 'cors';
    return 'network';
  } finally {
    window.clearTimeout(timer);
  }
};

const classifyMediaError = async (
  element: Element,
  mediaType: MediaType,
  url: string
): Promise<MediaErrorType> => {
  if (mediaType === 'video' && element instanceof HTMLVideoElement) {
    const fromVideo = classifyFromVideo(element);
    if (fromVideo) return fromVideo;
  }
  return classifyFromUrl(url);
};

export const trackMediaLoadErrorFromElement = (
  element: Element,
  reportId: string | null = activeReportId
): void => {
  if (!reportId) return;

  const mediaType = resolveMediaType(element);
  if (!mediaType) return;

  const mediaUrl = resolveMediaUrl(element);
  if (!mediaUrl) return;

  const sectionName = resolveSectionName(element);
  const key = trackingKey(mediaType, mediaUrl, sectionName);
  if (trackedKeys.has(key)) return;

  trackedKeys.add(key);

  void classifyMediaError(element, mediaType, mediaUrl).then((errorType) => {
    const tracked = trackEvent('media_load_error', {
      report_id: reportId,
      schema_version: POSTHOG_SCHEMA_VERSION,
      section_name: sectionName,
      media_type: mediaType,
      media_url: mediaUrl,
      error_type: errorType,
    });

    if (!tracked) {
      trackedKeys.delete(key);
    }
  });
};

const isBrokenImage = (image: HTMLImageElement): boolean =>
  Boolean(image.src) && image.complete && image.naturalWidth === 0;

const isBrokenVideo = (video: HTMLVideoElement): boolean => Boolean(video.error);

const scanFailedMedia = (root: ParentNode, reportId: string) => {
  root.querySelectorAll('img').forEach((image) => {
    if (isBrokenImage(image)) trackMediaLoadErrorFromElement(image, reportId);
  });
  root.querySelectorAll('video').forEach((video) => {
    if (isBrokenVideo(video)) trackMediaLoadErrorFromElement(video, reportId);
  });
};

export const startReportMediaLoadErrorTracking = (
  reportId: string,
  root: ParentNode = document
): (() => void) => {
  if (!reportId || typeof window === 'undefined') {
    return () => undefined;
  }

  activeReportId = reportId;

  const handleError = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (root instanceof Element && !root.contains(target) && root !== target) return;
    trackMediaLoadErrorFromElement(target, reportId);
  };

  window.addEventListener('error', handleError, true);
  scanFailedMedia(root, reportId);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          scanFailedMedia(node, reportId);
        }
      });
    });
  });

  const observeTarget =
    root instanceof Document ? root.documentElement : (root as Element);
  mutationObserver.observe(observeTarget, { childList: true, subtree: true });

  return () => {
    window.removeEventListener('error', handleError, true);
    mutationObserver.disconnect();
    if (activeReportId === reportId) {
      activeReportId = null;
    }
  };
};
